import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuItemThumbnail from '../../components/common/MenuItemThumbnail';
import {
  type AiCaptainMenuItem,
  formatCaptainPrice,
} from '../../utils/aiCaptainMenuItems';
import { requestCaptainPanelMinimize } from '../../utils/captainPanelEvents';
import { useVirtualCaptain } from '../context/VirtualCaptainContext';
import { DINE_IN_ORDER_TYPE } from '../types/events';

interface CaptainMenuItemCardsProps {
  items: AiCaptainMenuItem[];
  appName: string;
  restaurantId: number;
  hasMore?: boolean;
}

function StarRating({ rating }: { rating?: number }) {
  if (rating == null || rating <= 0) return null;
  const rounded = Math.round(rating * 10) / 10;
  return (
    <span className="vc-menu-card__rating" aria-label={`Rating ${rounded} out of 5`}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
      </svg>
      {rounded}
    </span>
  );
}

function VegDot({ itemType }: { itemType?: string }) {
  if (!itemType) return null;
  const isVeg = itemType.toLowerCase() === 'veg';
  return (
    <span
      className={`vc-menu-card__veg-dot${isVeg ? ' vc-menu-card__veg-dot--veg' : ' vc-menu-card__veg-dot--nonveg'}`}
      aria-label={isVeg ? 'Vegetarian' : 'Non-vegetarian'}
      title={isVeg ? 'Vegetarian' : 'Non-vegetarian'}
    />
  );
}

const CaptainMenuItemCards: React.FC<CaptainMenuItemCardsProps> = ({
  items,
  appName,
  restaurantId,
  hasMore,
}) => {
  const navigate = useNavigate();
  const { addMenuItemToCart, ongoingOrder, sendChatMessage } = useVirtualCaptain();
  const [addingId, setAddingId] = useState<number | null>(null);
  const [addedIds, setAddedIds] = useState<Set<number>>(() => new Set());
  const [errorById, setErrorById] = useState<Record<number, string>>({});

  const orderType = ongoingOrder?.orderType ?? DINE_IN_ORDER_TYPE;

  const openItemDetail = useCallback(
    (item: AiCaptainMenuItem) => {
      requestCaptainPanelMinimize();
      navigate(`/restaurants/${appName}/menu/${item.itemUrl || item.id}`);
    },
    [appName, navigate]
  );

  const handleAddToCart = useCallback(
    async (item: AiCaptainMenuItem) => {
      if (addingId != null || !addMenuItemToCart) return;
      setAddingId(item.id);
      setErrorById((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      try {
        const result = await addMenuItemToCart(item, orderType);
        if (result.ok) {
          setAddedIds((prev) => new Set(prev).add(item.id));
        } else {
          setErrorById((prev) => ({
            ...prev,
            [item.id]: result.message ?? 'Could not add to cart',
          }));
        }
      } finally {
        setAddingId(null);
      }
    },
    [addMenuItemToCart, addingId, orderType]
  );

  if (items.length === 0) return null;

  const visibleItems = items.filter((item) => item.availability !== false);
  if (visibleItems.length === 0) return null;

  return (
    <div className="vc-menu-cards" role="list" aria-label="Suggested menu items">
      {visibleItems.map((item) => {
        const isAdding = addingId === item.id;
        const justAdded = addedIds.has(item.id);
        const errorMessage = errorById[item.id];

        return (
          <article key={item.id} className="vc-menu-card" role="listitem">
            <button
              type="button"
              className="vc-menu-card__media"
              aria-label={`View ${item.name}`}
              onClick={() => openItemDetail(item)}
            >
              <MenuItemThumbnail
                restroId={restaurantId}
                menuId={item.id}
                filename={item.imageBig}
                className="vc-menu-card__thumb"
                imgClassName="vc-menu-card__img"
              />
            </button>

            <div className="vc-menu-card__body">
              <div className="vc-menu-card__top">
                <VegDot itemType={item.itemType} />
                <button
                  type="button"
                  className="vc-menu-card__name"
                  onClick={() => openItemDetail(item)}
                >
                  {item.name}
                </button>
                <StarRating rating={item.rating} />
              </div>

              {item.description && (
                <p className="vc-menu-card__desc">{item.description}</p>
              )}

              {(item.spiceLevel || (item.tags && item.tags.length > 0)) && (
                <div className="vc-menu-card__badges">
                  {item.spiceLevel && (
                    <span className="vc-menu-card__badge vc-menu-card__badge--spice">
                      {item.spiceLevel}
                    </span>
                  )}
                  {item.tags?.slice(0, 2).map((tag) => (
                    <span key={tag} className="vc-menu-card__badge vc-menu-card__badge--tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="vc-menu-card__meta">
                {item.offerPrice != null && item.price != null && item.offerPrice < item.price ? (
                  <span className="vc-menu-card__price-group">
                    <span className="vc-menu-card__price vc-menu-card__price--offer">
                      {formatCaptainPrice(item.offerPrice)}
                    </span>
                    <span className="vc-menu-card__price vc-menu-card__price--mrp">
                      {formatCaptainPrice(item.price)}
                    </span>
                  </span>
                ) : item.price != null ? (
                  <span className="vc-menu-card__price">{formatCaptainPrice(item.price)}</span>
                ) : null}
                {item.estimatedPrepMinutes != null && item.estimatedPrepMinutes > 0 && (
                  <span className="vc-menu-card__prep">
                    {item.estimatedPrepMinutes} min
                  </span>
                )}
              </div>

              {errorMessage && (
                <p className="vc-menu-card__error" role="alert">
                  {errorMessage}
                </p>
              )}

              <div className="vc-menu-card__actions">
                <button
                  type="button"
                  className="vc-menu-card__btn vc-menu-card__btn--ghost"
                  onClick={() => openItemDetail(item)}
                >
                  View
                </button>
                <button
                  type="button"
                  className={`vc-menu-card__btn vc-menu-card__btn--primary${
                    justAdded ? ' vc-menu-card__btn--success' : ''
                  }`}
                  disabled={isAdding || justAdded || !addMenuItemToCart}
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleAddToCart(item);
                  }}
                >
                  {isAdding ? 'Adding…' : justAdded ? 'Added' : 'Add to cart'}
                </button>
              </div>
            </div>
          </article>
        );
      })}

      {hasMore && sendChatMessage && (
        <button
          type="button"
          className="vc-menu-cards__show-more"
          onClick={() => sendChatMessage('show me more')}
        >
          Show more items
        </button>
      )}
    </div>
  );
};

export default CaptainMenuItemCards;
